// The people picker (17-people.md, "People picker"), shared by new chat, invite and block: the circle's
// people (new chat, invite; v1.58.37) and known people while nothing is typed, only the rows on screen
// built; typing filters them at once, then adds the chat server's user directory, a full chat ID typed,
// and (in a company with mail) colleagues from the company's people directory.
import { useVirtualizer } from '@tanstack/react-virtual';
import { RoomMemberEvent, type MatrixClient, type Room, type RoomMember } from 'matrix-js-sdk';
import { useEffect, useRef, useState } from 'react';
import { noAutofill } from '../blocks/Field';
import { Avatar } from '../blocks/Bits';
import { Button } from '../blocks/Button';
import { Icon } from '../blocks/icons';
import { Skeletons, SLOW_MS } from '../blocks/Wait';
import { circleName } from '../accounts/accounts';
import { normalise } from '../core/filter';
import { serverOf } from '../core/session';
import { mainSpace } from '../frame/Banner';
import { t } from '../i18n';
import { thumbnail } from '../session/media';
import { chatIcons, emojiIcons } from '../chat/icons';
import { colleagues, localPart, personName, personPicture, rememberPick, shownName } from './names';

export interface Person {
  userId?: string;
  name: string;
  email?: string;
  mxc?: string;
  /** From the company's people directory: may not have opened the chat yet. */
  colleague?: boolean;
}

const plain = (s: string) => normalise(s.normalize('NFD').replace(/\p{Diacritic}/gu, ''));
const notInChat = new Set<string>();
/** A to Z, without yourself and bridge or service accounts ("@_"). */
const aToZ = (client: MatrixClient, people: Iterable<Person>) => [...people].filter((p) => p.userId !== client.getUserId() && !p.userId!.startsWith('@_')).sort((a, b) => a.name.localeCompare(b.name));

/** Everyone the client knows: the people in the person's rooms. */
function knownPeople(client: MatrixClient): Person[] {
  const seen = new Map<string, Person>();
  for (const room of client.getRooms())
    if (room.getMyMembership() === 'join')
      for (const m of room.getJoinedMembers()) if (!seen.has(m.userId)) seen.set(m.userId, { userId: m.userId, name: personName(client, m.userId, room), mxc: personPicture(client, m.userId, room) });
  return aToZ(client, seen.values());
}

/**
 * The circle's people (17, v1.58.37): the joined members of the circle's main space, asked for with
 * joined_members while its own aren't all loaded (`people` undefined meanwhile), A to Z; a newcomer as
 * their join reaches this device. No main space: the people the client knows. Off: nothing is asked.
 */
export function useCirclePeople(client: MatrixClient, on = true): { people?: Person[]; space?: Room } {
  const space = on ? mainSpace(client) : undefined;
  const [asked, setAsked] = useState<{ room: string; joined: Record<string, { display_name?: string; avatar_url?: string }> }>();
  const [, redraw] = useState(0);
  useEffect(() => {
    if (!space) return;
    const changed = (_: unknown, m: RoomMember) => m.roomId === space.roomId && redraw((n) => n + 1);
    client.on(RoomMemberEvent.Membership, changed);
    if (space.getJoinedMembers().length < space.getJoinedMemberCount())
      void client.getJoinedRoomMembers(space.roomId).then((a) => a.joined, () => ({})).then((joined) => setAsked({ room: space.roomId, joined }));
    return () => void client.off(RoomMemberEvent.Membership, changed);
  }, [client, space]);
  if (!on) return {};
  if (!space) return { people: knownPeople(client) };
  const joined = asked?.room === space.roomId ? asked.joined : undefined;
  if (!joined && space.getJoinedMembers().length < space.getJoinedMemberCount()) return { space };
  const all = new Map<string, Person>();
  for (const [id, m] of Object.entries(joined ?? {}))
    if ((space.getMember(id)?.membership ?? 'join') === 'join') all.set(id, { userId: id, name: shownName(m.display_name) ?? personName(client, id, space), mxc: m.avatar_url || undefined });
  for (const m of space.getJoinedMembers()) all.set(m.userId, { userId: m.userId, name: personName(client, m.userId, space), mxc: personPicture(client, m.userId, space) });
  return { people: aToZ(client, all.values()), space };
}

export interface PickerProps {
  client: MatrixClient;
  /** The place's question, the field's accessible name. */
  question: string;
  /** People not offered (invite: those joined; block: those blocked). */
  leaveOut?: (userId: string) => boolean;
  /** A tag on a row, which can't be picked then ("Invited"). */
  tagOf?: (userId: string) => string | undefined;
  /** Colleagues from the company directory (new chat, invite). */
  withColleagues?: boolean;
  /** Offered first while nothing is typed. */
  first?: string;
  /** What to say for a colleague who hasn't opened the chat. */
  notInChatLine?: (name: string) => string;
  onPick: (person: Person) => Promise<unknown>;
  error?: string;
  /** Told whether a pick is being made and whether anything is typed (07, Closing a form page). */
  onState?: (state: { busy: boolean; touched: boolean }) => void;
}

export function Picker({ client, question, leaveOut, tagOf, withColleagues, first, notInChatLine, onPick, error, onState }: PickerProps) {
  const me = client.getUserId()!;
  const [text, setText] = useState('');
  const [found, setFound] = useState<{ query: string; people: Person[]; failed?: boolean }>();
  const [searching, setSearching] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [picking, setPicking] = useState(false);
  useEffect(() => onState?.({ busy: picking, touched: !!text }), [picking, text, onState]);
  const [problem, setProblem] = useState<{ line: string; email?: string }>();
  const [copied, setCopied] = useState(false);
  const query = text.trim();
  // The circle's people and the known people (17, v1.58.37): for new chat and invite, not block.
  const circle = useCirclePeople(client, !!withColleagues);
  const ours = (circle.space && circle.people) || [];
  const ourIds = new Set(ours.map((p) => p.userId));
  const others = knownPeople(client).filter((p) => !ourIds.has(p.userId));

  // 250 ms after the last key: the directory and colleagues, added to the known people who match.
  useEffect(() => {
    if (!query) return setFound(undefined);
    let live = true;
    const timer = setTimeout(async () => {
      setSearching(true);
      const q = plain(query);
      const [dir, team] = await Promise.all([
        // An answer after 25 s counts as failed (02, Loading).
        Promise.race([client.searchUserDirectory({ term: query, limit: 20 }), new Promise<never>((_, late) => setTimeout(late, SLOW_MS))]).then(
          (a) => a.results.map((r) => ({ userId: r.user_id, name: r.display_name || localPart(r.user_id), mxc: r.avatar_url })),
          () => undefined,
        ),
        withColleagues ? colleagues(client) : Promise.resolve([]),
      ]);
      if (!live) return;
      const mates = team.filter((c) => (plain(c.name).includes(q) || plain(c.email).includes(q)) && c.chatId !== me);
      setFound({ query, people: [...(dir ?? []), ...mates.map((c) => ({ userId: c.chatId ?? undefined, name: c.name, email: c.email, colleague: true }))], failed: !dir });
      setSearching(false);
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  // The rows: a heading is a string. Typed: a full chat ID, the known people who match at once, then
  // the directory's and colleagues' (one row per person, at most 30).
  const free = (p: Person) => p.userId !== me && (!p.userId || !leaveOut?.(p.userId));
  let rows: (Person | string)[];
  if (query) {
    const q = plain(query);
    const seen = new Set<string>();
    const typed: Person[] = /^@[^:\s]+:\S+$/.test(query) ? [{ userId: query, name: localPart(query) }] : [];
    const local = [...ours, ...others].filter((p) => plain(p.name).includes(q) || plain(p.userId!).includes(q));
    rows = [...typed, ...local, ...(found?.query === query ? found.people : [])].filter((p) => free(p) && !seen.has(p.userId ?? p.email!) && !!seen.add(p.userId ?? p.email!)).slice(0, 30);
  } else {
    const top = first ? [{ userId: first, name: personName(client, first), mxc: personPicture(client, first) }] : [];
    const [a, b] = [ours, others].map((list) => list.filter((p) => p.userId !== first && free(p)));
    const chatWith = t('people.youChatWith');
    rows = circle.space ? [t('people.inCircle', { circle: circleName(me) }), ...top, ...a, ...(b.length ? [chatWith, ...b] : [])] : [chatWith, ...top, ...b];
  }
  const people = rows.filter((r): r is Person => typeof r !== 'string');
  const names = new Map<string, number>();
  people.forEach((p) => names.set(p.name, (names.get(p.name) ?? 0) + 1));
  const box = useRef<HTMLDivElement>(null);
  const virtual = useVirtualizer({ count: rows.length, getScrollElement: () => box.current, estimateSize: () => 50, overscan: 10 });
  const blocked = (p: Person) => !!(p.userId && tagOf?.(p.userId)) || notInChat.has(p.email ?? p.userId ?? '');

  const pick = async (p: Person) => {
    if (picking || blocked(p)) return;
    setPicking(true);
    setProblem(undefined);
    try {
      // A colleague's chat account is asked for first; none: nothing is made or sent.
      if (!p.userId) throw new Error('no chat');
      // Only "no account there" (404) stops it; a server that won't show profiles to strangers (403) still lets the chat start.
      if (p.colleague) await client.getProfileInfo(p.userId).catch((e: { httpStatus?: number }) => { if (e.httpStatus === 404) throw e; });
      // The new chat shows the name and picture they were picked by until their own arrive (17).
      rememberPick(client, p.userId, p.name, p.mxc);
      await onPick(p);
    } catch (e) {
      if (p.colleague && (!p.userId || (e as { httpStatus?: number }).httpStatus === 404 || (e as Error).message === 'no chat')) {
        notInChat.add(p.email ?? p.userId ?? '');
        setProblem({ line: notInChatLine?.(p.name) ?? '', email: p.email });
      }
    } finally {
      setPicking(false);
    }
  };

  return (
    <div className="picker-people">
      {(error || problem) && (
        <p className="picker-error" role="alert">
          <Icon svg={chatIcons.warning} size={16} />
          {error ?? problem?.line}
          {problem?.email && (
            <Button small onClick={() => void navigator.clipboard.writeText(problem.email!).then(() => (setCopied(true), setTimeout(() => setCopied(false), 2000)))}>
              {t(copied ? 'common.copied' : 'people.copyAddress')}
            </Button>
          )}
        </p>
      )}
      <label className="picker-field">
        <Icon svg={emojiIcons.search} size={18} />
        <input
          {...noAutofill}
          value={text}
          placeholder={t('people.name')}
          aria-label={question}
          autoFocus
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            const firstFree = people.find((p) => !blocked(p));
            if (firstFree) void pick(firstFree);
          }}
        />
      </label>
      {!query && !people.length && !(circle.space && !circle.people) && <p className="picker-note">{t('people.typeName')}</p>}
      {found?.failed && (
        <p className="picker-note">
          {t('people.directoryFailed')}{' '}
          <button type="button" className="link-button" onClick={() => setAttempt((a) => a + 1)}>
            {t('common.retry')}
          </button>
        </p>
      )}
      {query && found?.query === query && !people.length && !found.failed && <p className="picker-note">{t('people.noneFound', { text: query })}</p>}
      <div ref={box} role="group" aria-label={t('people.people')} className="picker-rows" aria-busy={searching || undefined}>
        <div style={{ height: virtual.getTotalSize(), position: 'relative' }}>
          {virtual.getVirtualItems().map((v) => {
            const p = rows[v.index];
            const at = { 'data-index': v.index, ref: virtual.measureElement, style: { transform: `translateY(${v.start}px)` } };
            if (typeof p === 'string') return <h3 key={p} {...at} className="picker-item picker-heading">{p}</h3>;
            const tag = p.userId ? tagOf?.(p.userId) : undefined;
            const missing = notInChat.has(p.email ?? p.userId ?? '');
            const second = missing
              ? t('people.notInChat')
              : p.userId && serverOf(p.userId) !== serverOf(me)
                ? p.userId
                : p.userId && (names.get(p.name) ?? 0) > 1
                  ? p.userId.slice(1).split(':')[0]
                  : undefined;
            return (
              <div key={p.userId ?? p.email} {...at} className="picker-item">
                <button type="button" className="picker-row" data-desyr-person-pick={p.userId ?? ''} disabled={picking || !!tag || missing} onClick={() => void pick(p)}>
                  <Avatar id={p.userId ?? p.email ?? p.name} name={p.name} src={thumbnail(client, p.mxc, 32)} size={32} />
                  <span>
                    <span className="picker-name">{p.name}</span>
                    {second && <span className="picker-second">{second}</span>}
                  </span>
                  {tag && <span className="file-type">{tag}</span>}
                </button>
              </div>
            );
          })}
        </div>
        {/* Skeleton rows under the known people while the directory or the circle's people answer (17, v1.58.24). */}
        {(searching || (!query && circle.space && !circle.people)) && <Skeletons count={4} round bars={2} className="picker-row" />}
      </div>
    </div>
  );
}
